import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBanner } from './ui';
import { Button, NumberInput, TextInput } from '../neon/controls';
import { useDomId } from '../hooks/useDomId';
import type { FieldDef } from './EntityForm';

export interface CreateEntityProps {
  title: string;
  fields: FieldDef[];
  create: (body: Record<string, any>) => Promise<string>;
  onCreated: (id: string) => void;
  backTo: string;
}

export function CreateEntity({ title, fields, create, onCreated, backTo }: CreateEntityProps) {
  const navigate = useNavigate();
  const formId = useDomId('create');
  const [form, setForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, any> = {};
      for (const f of fields) {
        const v = form[f.key];
        if (f.type === 'number') body[f.key] = v === '' || v == null ? null : Number(v);
        else body[f.key] = v ?? '';
      }
      const id = await create(body);
      onCreated(id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>{title}</h2>
        <Button id={`${formId}-back`} text="Back" variant="tertiary" icon="arrow-left" onClick={() => navigate(backTo)} />
      </div>
      <ErrorBanner error={error} />
      <form
        className="entity-form"
        onSubmit={(e) => {
          e.preventDefault();
          void doCreate();
        }}
      >
        <div className="form-grid">
          {fields.map((f) => {
            const fid = `${formId}-${f.key}`;
            return f.type === 'number' ? (
              <NumberInput
                key={f.key}
                id={fid}
                label={f.label}
                value={form[f.key] ?? undefined}
                onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
              />
            ) : (
              <TextInput
                key={f.key}
                id={fid}
                label={f.label}
                value={form[f.key] ?? ''}
                onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
                helper={f.hint}
              />
            );
          })}
        </div>
        <div className="form-actions">
          <Button id={`${formId}-create`} text={saving ? 'Creating…' : 'Create'} onClick={() => void doCreate()} loading={saving} />
        </div>
      </form>
    </div>
  );
}
